import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const SetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setToken, setUser, API } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [loadingEmail, setLoadingEmail] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    // Fetch transaction to get email
    const fetchEmail = async () => {
      try {
        const response = await axios.get(`${API}/payment/status/${sessionId}`);
        const email = response.data?.metadata?.guest_email || '';
        setUserEmail(email);
      } catch (err) {
        console.error('Error fetching email:', err);
      } finally {
        setLoadingEmail(false);
      }
    };

    fetchEmail();
  }, [sessionId, navigate, API]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/set-password-guest`, {
        session_id: sessionId,
        password: password
      });

      // Show success popup
      setShowSuccess(true);

      // Set auth state with returned token and user
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);

      // Wait a moment for success message, then redirect
      setTimeout(() => {
        navigate('/welcome');
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set password. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        backgroundImage: 'url(/assets/landing-background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Success Popup */}
      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 shadow-2xl animate-fade-in">
            <div className="text-center">
              <div className="text-6xl mb-4">✅</div>
              <h2 
                className="text-2xl font-bold mb-2 text-gray-800"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
              >
                Account Created Successfully!
              </h2>
              <p 
                className="text-gray-600"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
              >
                Redirecting you to your welcome page...
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-md w-full">
        <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl p-8">
          <h1 
            className="text-3xl font-bold text-center mb-2 text-gray-800"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
          >
            Create Your Account
          </h1>
          <p 
            className="text-center text-gray-600 mb-6"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
          >
            Payment successful! Set a password to access your journaling session.
          </p>

          <form onSubmit={handleSetPassword} className="space-y-4">
            <div>
              <label 
                className="block text-sm font-medium text-gray-700 mb-1"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
              >
                Email
              </label>
              <input
                type="email"
                value={userEmail}
                readOnly
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
                disabled={loadingEmail}
              />
              {loadingEmail && (
                <p className="text-xs text-gray-500 mt-1">Loading...</p>
              )}
            </div>

            <div>
              <label 
                className="block text-sm font-medium text-gray-700 mb-1"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
              >
                Create Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
                placeholder="Enter password (min 6 characters)"
                required
                disabled={loading || loadingEmail}
              />
            </div>

            <div>
              <label 
                className="block text-sm font-medium text-gray-700 mb-1"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
              >
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
                placeholder="Confirm your password"
                required
                disabled={loading || loadingEmail}
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || loadingEmail}
              className="w-full bg-gray-800 text-white py-3 rounded-lg hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              {loading ? 'Creating Account...' : 'Create Account & Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
